// PRD 07 §2.2 #17 — WYSIWYG editor for Text content blocks.
// Built on `document.execCommand` to match the prototype 1:1 (see
// prototype/admin/pages/management-guidelines.html `richEditorHtml` /
// `bindRichEditor`). execCommand is deprecated in modern browsers but still
// works everywhere we ship; long-term replacement candidate is Tiptap.
//
// Toolbar (left → right):
//   Paragraph (Body / H1–H3) · Size (Default/S/L/XL) · | ·
//   B / I / U / S · Color palette (6 swatches) · | ·
//   Bullet list / Numbered list · | ·
//   Align L / C / R · | · Link · Quote
//
// Editor is `contenteditable` and writes back to the parent via `onChange`
// whenever the user types or runs a toolbar command. The component is
// uncontrolled internally — `value` only seeds the DOM on first mount and
// when the parent swaps the block identity (e.g. switching from create to
// edit mode), so the user's cursor isn't yanked back on every keystroke.

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/components/feedback/useToast';
import { isHttpsUrl } from '@/shared/utils/block-validation';
import { useRichTextSize } from '@/shared/hooks/use-rich-text-size';
import { hasNonHttpsLink, normalizeRichHtml } from '@/shared/utils/normalize-rich-html';
import { TEXT_SIZES } from '@/shared/utils/rich-text-format';

// 6-swatch palette — must match the prototype.
const COLORS: ReadonlyArray<{ value: string; titleKey: string }> = [
  { value: '#111827', titleKey: 'editor.colors.black' },
  { value: '#DC2626', titleKey: 'editor.colors.red' },
  { value: '#D97706', titleKey: 'editor.colors.orange' },
  { value: '#16A34A', titleKey: 'editor.colors.green' },
  { value: '#2563EB', titleKey: 'editor.colors.blue' },
  { value: '#7C3AED', titleKey: 'editor.colors.purple' },
];

interface RichTextEditorProps {
  // The block id is used as the React key by the parent; the editor uses it
  // as a stability signal — when it changes, the DOM is re-seeded from `value`.
  blockId: number;
  value: string;
  onChange: (html: string) => void;
  placeholder: string;
  // SHCT-582 — read-only body for a METADATA:READ operator (PRD 21 §7.4 P-2): the toolbar goes
  // away and the content area stops being editable. Same shape as the counseling-manual twin,
  // which already had it; this editor was the one that let a READ-only Role retype a guideline.
  readOnly?: boolean;
}

function isEditorEmpty(el: HTMLElement): boolean {
  if (!el.innerHTML || el.innerHTML === '<br>') return true;
  if (el.querySelector('img,video,iframe')) return false;
  return !el.textContent?.trim();
}

export function RichTextEditor({
  blockId,
  value,
  onChange,
  placeholder,
  readOnly = false,
}: RichTextEditorProps) {
  const { t } = useTranslation('management-guidelines');
  const contentRef = useRef<HTMLDivElement>(null);
  const paletteRef = useRef<HTMLSpanElement>(null);
  // Last caret/selection inside the editor. Toolbar `<select>` popups steal
  // focus from the contenteditable (they need their native mousedown, unlike
  // the buttons), which collapses the live selection — so we stash the range
  // on every edit and restore it before running a command.
  const savedRange = useRef<Range | null>(null);

  // Seed the contenteditable DOM on mount, and again when the block identity
  // changes. NOT on every value change — that would reset the caret while
  // the user is typing.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if (el.innerHTML !== value) el.innerHTML = value;
    el.dataset.empty = isEditorEmpty(el) ? 'true' : 'false';
  }, [blockId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close the color palette when the user clicks outside it. The other
  // toolbar buttons close it implicitly (their handlers always close before
  // running execCommand) — this listener only catches stray document clicks.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const palette = paletteRef.current;
      if (!palette) return;
      if (!palette.classList.contains('--open')) return;
      if (palette.contains(e.target as Node)) return;
      palette.classList.remove('--open');
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const commit = () => {
    const el = contentRef.current;
    if (!el) return;
    el.dataset.empty = isEditorEmpty(el) ? 'true' : 'false';
    // SHCT-473 — this editor was never given the SHCT-453 normalization (only
    // health-encyclopedia and counseling-manual were), so execCommand's legacy
    // tags AND pasted inline styles both reached the BE sanitizer and were
    // stripped. Same canonical-tag conversion as the other two editors.
    // SHCT-370 Change 4 — `httpsOnlyLinks` mirrors `GuidelineService`'s sanitizeHttpsOnly, so an
    // href the save would delete never survives the editor in the first place.
    // SHCT-871 — `explicitDefaultSize` stamps 기본(16px) onto text the author never resized.
    // Without it the patient app renders that text at its own 14px fallback, so 기본 came out
    // looking like 작게. Only the committed value is stamped; a paste is normalized without it
    // and picks the size up from the commit that follows.
    onChange(normalizeRichHtml(el.innerHTML, { httpsOnlyLinks: true, explicitDefaultSize: true }));
  };

  // Remember the current selection while it still lives inside the editor.
  const saveSelection = () => {
    const el = contentRef.current;
    if (!el) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (el.contains(range.commonAncestorContainer)) {
      savedRange.current = range.cloneRange();
    }
  };

  const restoreSelection = () => {
    const range = savedRange.current;
    if (!range) return;
    const sel = window.getSelection();
    if (!sel) return;
    sel.removeAllRanges();
    sel.addRange(range);
  };

  // SHCT-871 — 크기 dropdown state: disabled inside a 소제목, and showing the size written at the caret.
  const { sizeKey, sizeLocked, paragraphTag, applySize, refresh } =
    useRichTextSize(contentRef, commit);

  const exec = (command: string, arg?: string) => {
    const el = contentRef.current;
    if (!el) return;
    el.focus();
    // Re-apply the pre-command selection so toolbar `<select>` interactions
    // (which moved focus to the popup) still format the intended text.
    restoreSelection();
    // execCommand is deprecated but every Chromium/WebKit/Gecko build still
    // implements it. When it disappears, swap this for Tiptap/Slate.
    document.execCommand(command, false, arg);
    commit();
    // SHCT-871 — formatBlock changes the BLOCK under the caret without moving the selection, so
    // `selectionchange` is not guaranteed to fire and the 크기 lock would not notice that the line
    // just became a 소제목. Recompute after every command: picking 소제목 from the 문단 dropdown is
    // the route an author actually takes to a heading, and QC re-tested on exactly it.
    refresh();
    saveSelection();
  };

  // SHCT-473 — a paste carries its formatting as inline CSS (`font-weight: 700`,
  // `color: rgb(…)`), which the BE style allow-list drops on save. Normalize the
  // clipboard HTML to the canonical tags BEFORE it enters the DOM, so the editor,
  // the preview and the stored value all agree. Plain-text-only pastes need no
  // handling — the browser's own insertion is already canonical.
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const html = e.clipboardData.getData('text/html');
    if (!html) return;
    e.preventDefault();
    // SHCT-370 Change 4 — say so before dropping the link. Same message the link button uses for a
    // typed http:// URL: it is the same rule being applied, so it gets the same words rather than
    // new copy no PRD defines.
    if (hasNonHttpsLink(html)) toast.error(t('editor.linkHttpsOnly'));
    document.execCommand('insertHTML', false, normalizeRichHtml(html, { httpsOnlyLinks: true }));
    commit();
    saveSelection();
  };

  const handleColorClick = (color: string) => {
    paletteRef.current?.classList.remove('--open');
    exec('foreColor', color);
  };

  const handleLink = () => {
    // eslint-disable-next-line no-alert
    const url = window.prompt(t('editor.linkPrompt'), 'https://');
    if (!url) return;
    // SHCT-370 Change 4 / PRD 07 §3 line 129 — 하이퍼링크는 `https://` 유효 URL만 (http·무효 거부).
    // This editor used to insert whatever the prompt returned, so an http:// link was authored, saved
    // and served: the backend's block validator checks the VIDEO block's URL only, never an href inside
    // the text HTML, so nothing downstream would have caught it. 건강백과 and 상담매뉴얼 already refuse
    // it here — the rule is one rule for the three screens (PRD 07 v1.9 "3곳 프로토 동일 구현").
    if (!isHttpsUrl(url)) {
      toast.error(t('editor.linkHttpsOnly'));
      return;
    }
    exec('createLink', url.trim());
  };

  const togglePalette = (e: React.MouseEvent) => {
    e.stopPropagation();
    const palette = paletteRef.current;
    if (!palette) return;
    palette.classList.toggle('--open');
  };

  return (
    <div className="mg-rte-wrap" data-rte-wrap={blockId}>
      {readOnly ? null : (
      <div
        className="mg-rte-toolbar"
        onMouseDown={(e) => {
          // Keep focus (and the selection) in the editor for buttons, but let
          // the native <select> popups open — preventDefault on their mousedown
          // would stop the dropdown from ever appearing.
          if ((e.target as HTMLElement).tagName !== 'SELECT') e.preventDefault();
        }}
      >
        {/* SHCT-871 / PRD 10 §3.7 — 문단 2종. 제목1·제목3 are retired; anything left in old
            content is folded into 소제목 by normalizeRichHtml on the next save. */}
        <select
          className="mg-rte-sel"
          title={t('editor.toolbar.paragraph')}
          // SHCT-871 — controlled, so the control names the block the caret is in rather than
          // the last value picked. The tag is passed bare, as the prototype's rteText.applyFormat
          // does; this editor alone wrapped it in angle brackets.
          value={paragraphTag}
          onChange={(e) => exec('formatBlock', e.target.value)}
        >
          <option value="p">{t('editor.paragraphs.body')}</option>
          <option value="h2">{t('editor.paragraphs.subheading')}</option>
        </select>

        {/* SHCT-871 — 본문 크기 4단계, written as an explicit span (execCommand's fontSize emits
            <font>, which is not in the storage allow-list and vanishes on save). 소제목 has a fixed
            size, so the dropdown is disabled while the caret sits inside one. */}
        <select
          className="mg-rte-sel"
          // SHCT-871 — say WHY it is unusable, as the prototype's rteText.applyFormat does.
          title={t(sizeLocked ? 'editor.toolbar.sizeLocked' : 'editor.toolbar.size')}
          value={sizeKey}
          disabled={sizeLocked}
          onChange={(e) => applySize(e.target.value)}
        >
          {TEXT_SIZES.map((s) => (
            <option key={s.key} value={s.key}>{t(`editor.sizes.${s.key}`)}</option>
          ))}
        </select>

        <span className="mg-rte-div" />

        <button
          type="button"
          className="mg-rte-btn"
          onClick={() => exec('bold')}
          title={t('editor.toolbar.bold')}
          style={{ fontWeight: 700 }}
        >
          B
        </button>
        <button
          type="button"
          className="mg-rte-btn"
          onClick={() => exec('italic')}
          title={t('editor.toolbar.italic')}
          style={{ fontStyle: 'italic' }}
        >
          I
        </button>
        <button
          type="button"
          className="mg-rte-btn"
          onClick={() => exec('underline')}
          title={t('editor.toolbar.underline')}
          style={{ textDecoration: 'underline' }}
        >
          U
        </button>
        <button
          type="button"
          className="mg-rte-btn"
          onClick={() => exec('strikeThrough')}
          title={t('editor.toolbar.strike')}
          style={{ textDecoration: 'line-through' }}
        >
          S
        </button>

        <span className="mg-rte-color-wrap">
          <button
            type="button"
            className="mg-rte-btn"
            onClick={togglePalette}
            title={t('editor.toolbar.color')}
          >
            <span style={{ fontWeight: 700 }}>A</span>▾
          </button>
          <span className="mg-rte-color-palette" ref={paletteRef}>
            {COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                className="mg-rte-swatch"
                style={{ background: c.value }}
                title={t(c.titleKey)}
                onClick={(e) => {
                  e.stopPropagation();
                  handleColorClick(c.value);
                }}
              />
            ))}
          </span>
        </span>

        <span className="mg-rte-div" />

        <button
          type="button"
          className="mg-rte-btn"
          onClick={() => exec('insertUnorderedList')}
          title={t('editor.toolbar.bulletList')}
        >
          •
        </button>
        <button
          type="button"
          className="mg-rte-btn"
          onClick={() => exec('insertOrderedList')}
          title={t('editor.toolbar.numberedList')}
        >
          1.
        </button>

        <span className="mg-rte-div" />

        <button
          type="button"
          className="mg-rte-btn"
          onClick={() => exec('justifyLeft')}
          title={t('editor.toolbar.alignLeft')}
        >
          ◀
        </button>
        <button
          type="button"
          className="mg-rte-btn"
          onClick={() => exec('justifyCenter')}
          title={t('editor.toolbar.alignCenter')}
        >
          ≡
        </button>
        <button
          type="button"
          className="mg-rte-btn"
          onClick={() => exec('justifyRight')}
          title={t('editor.toolbar.alignRight')}
        >
          ▶
        </button>

        <span className="mg-rte-div" />

        <button
          type="button"
          className="mg-rte-btn"
          onClick={handleLink}
          title={t('editor.toolbar.link')}
        >
          🔗
        </button>
        <button
          type="button"
          className="mg-rte-btn"
          onClick={() => exec('formatBlock', '<blockquote>')}
          title={t('editor.toolbar.quote')}
        >
          &ldquo;
        </button>
      </div>
      )}

      <div
        ref={contentRef}
        className="mg-rte-content"
        contentEditable={!readOnly}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        data-empty="true"
        onInput={readOnly ? undefined : commit}
        onBlur={readOnly ? undefined : commit}
        onPaste={readOnly ? undefined : handlePaste}
        onKeyUp={readOnly ? undefined : saveSelection}
        onMouseUp={readOnly ? undefined : saveSelection}
      />
    </div>
  );
}
